import React, { type ChangeEvent, type ReactNode, useMemo, useState } from 'react';

import { FormattedMessage } from 'react-intl-next';

import { Checkbox } from '@atlaskit/checkbox';
import { cssMap } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { Label } from '@atlaskit/form';
import { useLocalStorage } from '@atlaskit/frontend-utilities';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import { Box, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import {
	hasProductPermission,
	useProductPermissions,
} from '@atlaskit/teams-app-internal-product-permissions';
import { token } from '@atlaskit/tokens';

import { messages } from '../i18n/messages';
import { SkeletonItem } from '../SkeletonItem';

import { ContainerType, type CreateContainersProps } from './types';

const localStorageKey = 'create-team-dialog-create-container-section';

const styles = cssMap({
	stack: {
		marginTop: token('space.100'),
	},
	checkboxContainer: {
		marginTop: token('space.100'),
	},
	label: {
		cursor: 'pointer',
	},
	button: {
		textAlign: 'left',
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		backgroundColor: 'transparent',
	},
});

const chevronProps = {
	size: 'small',
	label: '',
} as const;

const productConfig = [
	{ key: ContainerType.JIRA_PROJECT, product: 'jira', label: messages.createContainersJira },
	{
		key: ContainerType.CONFLUENCE_SPACE,
		product: 'confluence',
		label: messages.createContainersConfluence,
	},
	{ key: ContainerType.LOOM_SPACE, product: 'loom', label: messages.createContainersLoom },
] as const;

function CreateContainers({
	cloudId,
	userId,
	onChange,
}: CreateContainersProps): React.JSX.Element | null {
	const [hasSeenThis] = useLocalStorage<boolean>(localStorageKey, false);
	const [isOpen, setIsOpen] = useState(hasSeenThis ? false : true);
	const [selectedContainers, setSelectedContainers] = useState<ContainerType[]>([]);

	const { data: productPermissions, loading } = useProductPermissions({
		userId,
		cloudId,
		permissionsToCheck: {
			jira: ['CREATE_PROJECT'],
			loom: ['write'],
			confluence: ['CREATE_SPACE'],
		},
	});

	const containers = useMemo(() => {
		const checkPermission = (container: ContainerType) => {
			if (!productPermissions) {
				return false;
			}

			const product = productConfig.find((p) => p.key === container)?.product;
			if (!product) {
				return false;
			}
			return productPermissions ? hasProductPermission(productPermissions, product) : false;
		};

		return productConfig
			.filter((product) => checkPermission(product.key))
			.map((product) => ({ value: product.key, label: product.label }));
	}, [productPermissions]);

	const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { value, checked } = event.target;
		const container = value as unknown as ContainerType;

		setSelectedContainers((prev) => {
			const newContainers = checked
				? [...prev, container]
				: prev.filter((item) => item !== container);
			onChange(newContainers);
			return newContainers;
		});
	};

	if (loading) {
		return <SkeletonItem testId="create-container-skeleton" />;
	}

	if (!containers || containers.length === 0) {
		return null;
	}

	return (
		<ExperimentCheck>
			<Stack xcss={styles.stack}>
				<Pressable xcss={styles.button} onClick={() => setIsOpen(!isOpen)}>
					<Label htmlFor="team-spaces">
						<Inline alignBlock="baseline" space="space.050" xcss={styles.label}>
							<FormattedMessage {...messages.createContainersLabel} />
							{isOpen ? <ChevronUpIcon {...chevronProps} /> : <ChevronDownIcon {...chevronProps} />}
						</Inline>
					</Label>
				</Pressable>
				{isOpen ? (
					<>
						<Text size="small" color="color.text.subtlest">
							<FormattedMessage {...messages.createContainersInfo} />
						</Text>
						<Box xcss={styles.checkboxContainer}>
							{containers.map((product) => (
								<Checkbox
									key={product.value}
									label={
										<Text size="small">
											<FormattedMessage {...product.label} />
										</Text>
									}
									value={product.value}
									name={`container-${product.value}`}
									onChange={handleChange}
									isChecked={selectedContainers.includes(product.value)}
								/>
							))}
						</Box>
					</>
				) : null}
			</Stack>
		</ExperimentCheck>
	);
}

function ExperimentCheck({ children }: { children: ReactNode }) {
	const enrolledInExperiment = useMemo(() => {
		if (FeatureGates.initializeCalled()) {
			return FeatureGates.getExperimentValue(
				'teams_app_auto_container_create_universal_create',
				'isEnabled',
				false,
				{ fireExperimentExposure: true },
			);
		}
		return false;
	}, []);

	if (enrolledInExperiment) {
		return <>{children}</>;
	}

	return null;
}

export { CreateContainers, localStorageKey as haveISeenContainersKey };
